import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getSeriesBySlug } from '../services/api';
import EventCard from '../components/EventCard';
import Navbar from '../components/Navbar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTheme } from '../context/ThemeContext';
import useWindowSize from '../hooks/useWindowSize';

function SeriesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  useEffect(() => {
    getSeriesBySlug(slug)
      .then(res => {
        setSeries(res.data.series);
        setEvents(res.data.events);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Cargando...</p>
    </div>
  );

  if (!series) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Colección no encontrada</p>
    </div>
  );

  const nextEvent = events[0];
  const heroImage = series.banner_image || nextEvent?.banner_image || series.cover_image || nextEvent?.image;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Helmet>
        <title>{series.name} - GaliTicket</title>
        <meta name="description" content={series.description || `Todas las fechas disponibles de ${series.name}`} />
      </Helmet>

      <Navbar />

      {heroImage && (
        <div style={{
          width: '100%',
          ...(isMobile ? { height: '200px' } : { height: 'min(32vw, 340px)' }),
          overflow: 'hidden',
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }} />
      )}

      <div style={{ padding: isMobile ? '1.5rem 1rem' : '2.5rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <button style={{ backgroundColor: 'transparent', border: 'none', color: colors.textSecondary, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '1.2rem' }} onClick={() => navigate('/')}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Volver
        </button>

        <span style={{ backgroundColor: colors.accent, color: colors.accentText, padding: '0.25rem 0.9rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', width: 'fit-content', display: 'inline-block', marginBottom: '0.8rem' }}>
          Colección
        </span>

        <h1 style={{ color: colors.text, fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{series.name}</h1>

        {series.description && (
          <p style={{ color: colors.textSecondary, fontSize: '0.95rem', marginBottom: '1rem', maxWidth: '700px', lineHeight: 1.6 }}>{series.description}</p>
        )}

        {nextEvent && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '0.8rem' : '1.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <CalendarTodayIcon style={{ fontSize: 16, color: colors.accent }} />
              <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
                Próxima: {new Date(nextEvent.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <LocationOnIcon style={{ fontSize: 16, color: colors.accent }} />
              <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
                {nextEvent.location}
              </p>
            </div>
          </div>
        )}

        <h2 style={{ color: colors.text, fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
          {events.length === 1 ? 'Próxima fecha' : `Todas las fechas (${events.length})`}
        </h2>

        {events.length === 0 ? (
          <p style={{ color: colors.textSecondary }}>No hay fechas disponibles actualmente</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? '0' : '1.5rem' }}>
            {events.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SeriesPage;